import { useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { SkeletonValue } from 'modules/finance/components/FinancialWorkspace/components/SkeletonValue'

import { ProgressCircle } from './ProgressCircle'
import { StatusCircle } from './StatusCircle'
import { ActivationCenterResourceType } from './enums'
import { ActivationCenterStatusesLabels, calculateStatus } from './statuses'

interface ActivationCenterResource {
  label: string
  type: ActivationCenterResourceType
  button?: React.ReactNode
  sourceId: string
}

interface ActivationCenterProps {
  resources: ActivationCenterResource[]
  loading: boolean
  defaultOpen?: boolean
}

export const ActivationCenter = ({ resources, loading, defaultOpen }: ActivationCenterProps) => {
  const [opened, setOpened] = useState(defaultOpen)

  if (resources.length === 0) return null

  const status = calculateStatus(
    resources.length,
    resources.filter((resource) => resource.type === ActivationCenterResourceType.FILLED).length
  )
  const statusLabel = ActivationCenterStatusesLabels()[status]

  const handleClick = (isOpen: boolean) => {
    if (isOpen) {
      track(trackingEvents.accounting.newFinancialWorkspace.activationCenter.opened)
    }

    setOpened(isOpen)
  }

  return (
    <PopoverMenu type="dialog" onClick={handleClick} defaultOpen={defaultOpen}>
      <PopoverMenu.Trigger disabled={loading}>
        <Box
          height="s32"
          flexDirection="row"
          alignItems="center"
          gap="s8"
          paddingRight="s12"
          paddingLeft="s8"
          border={{ all: { color: 'grey400', style: 'solid', width: 's2' } }}
          borderRadius={{ all: 'abs016' }}
          cursor="pointer"
          {...(!loading && { 'data-testid': 'activation-center-trigger' })}
          {...(opened && { background: 'grey100' })}
        >
          <ProgressCircle type="small" status={status} disabled={loading} />
          {loading ? (
            <SkeletonValue width="s96" height="s16" />
          ) : (
            <Text.Normal size="200" weight="semibold">
              {statusLabel}
            </Text.Normal>
          )}
          <Icon.Medium icon="chevron-down" />
        </Box>
      </PopoverMenu.Trigger>
      <PopoverMenu.Content disablePortal side="bottom" align="end" width="s360">
        <Box paddingX="s24" paddingY="s12" gap="s8" data-testid="activation-center-content">
          <Box flexDirection="row" alignItems="center" gap="s8">
            <ProgressCircle type="regular" status={status} />
            <Text.Normal size="300" weight="medium">
              {statusLabel}
            </Text.Normal>
          </Box>

          <Text.Normal size="100" color="textSecondary" weight="normal">
            {i18n.t('finance.financial_workspace.activation_center.description')}
          </Text.Normal>

          <Box flexDirection="column" alignItems="center" gap="s8" paddingTop="s12">
            {resources.map((resource, index) => (
              <Box
                key={index}
                flexDirection="row"
                alignItems="center"
                width="full"
                gap="s8"
                minHeight="s32"
                justifyContent="spaceBetween"
                data-testid={resource.sourceId}
              >
                <Box flexDirection="row" alignItems="flexStart" gap="s8">
                  <Box width="s16" height="s16">
                    <StatusCircle type={resource.type} />
                  </Box>
                  <Text.Normal size="100" color="textSecondary" weight="normal">
                    {resource.label}
                  </Text.Normal>
                </Box>
                {resource.button}
              </Box>
            ))}
          </Box>
        </Box>
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}
